import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Quote, Star, Heart, CheckCircle } from 'lucide-react';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import { useLanguage } from '../i18n/LanguageContext';
import { JsonLd } from '@/components/JsonLd';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Testimonials() {
  const navigate = useNavigate();
  const { language } = useLanguage();

  const [testimonials, setTestimonials] = useState([]);
  const [satisfaction, setSatisfaction] = useState(0);

  useEffect(() => {
    let active = true;
    axios.get(`${API}/testimonials`)
      .then(({ data }) => {
        if (!active) return;
        const mapped = (data.testimonials || []).map((t) => ({
          id: t.id,
          name: t.name,
          age: t.age,
          location: t.location,
          rating: t.rating,
          text: t.story,
          date: t.created_at,
          highlight: 'Témoignage vérifié',
          photo: `https://ui-avatars.com/api/?name=${encodeURIComponent(t.name)}&background=ec4899&color=fff&size=128&bold=true`,
        }));
        setTestimonials(mapped);
        setSatisfaction(data.satisfaction || 0);
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const averageRating = testimonials.length > 0 ? (testimonials.reduce((sum, t) => sum + t.rating, 0) / testimonials.length).toFixed(1) : "0.0";
  const totalReviews = testimonials.length;
  // Only emit review/rating structured data when we actually have valid reviews.
  // Otherwise Google flags "reviewCount must be positive" and "rating out of range".
  const hasReviews = totalReviews > 0 && parseFloat(averageRating) >= 1;

  const metaDescription = hasReviews
    ? `Découvrez les témoignages de ${totalReviews} utilisateurs satisfaits d'A-More AI. Note moyenne : ${averageRating}/5. Des histoires d'amour réelles grâce à l'intelligence artificielle.`
    : "Découvrez les témoignages d'utilisateurs d'A-More AI : des histoires d'amour réelles nées grâce à l'intelligence artificielle.";

  const reviewSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "A-More AI",
    "description": "Application de rencontre utilisant l'intelligence artificielle pour créer des connexions authentiques",
    "brand": {
      "@type": "Brand",
      "name": "A-More AI"
    },
    ...(hasReviews ? {
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": averageRating,
        "reviewCount": totalReviews,
        "bestRating": "5",
        "worstRating": "1"
      },
      "review": testimonials.map(t => ({
        "@type": "Review",
        "author": {
          "@type": "Person",
          "name": t.name
        },
        "datePublished": t.date,
        "reviewBody": t.text,
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": t.rating,
          "bestRating": "5",
          "worstRating": "1"
        }
      }))
    } : {})
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${i < rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
      />
    ));
  };

  return (
    <>
      <Helmet>
        <title>Témoignages | A-More AI - Avis Clients Vérifiés</title>
        <meta name="description" content={metaDescription} />
        <meta name="keywords" content="témoignages A-More AI, avis clients, success stories, rencontres réussies, couples formés, avis application rencontre IA" />
        <link rel="canonical" href={`${window.location.origin}/${language}/testimonials`} />
      </Helmet>

      <JsonLd data={reviewSchema} />

      <div className="min-h-screen py-8 px-6">
        <div className="max-w-6xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-pink-600 mb-6 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Retour
          </button>

          <div className="glass-card p-8 mb-8 text-center">
            <div className="flex items-center justify-center mb-4">
              <Heart className="w-16 h-16 text-pink-500 fill-pink-500" />
            </div>
            <h1 className="text-4xl font-bold gradient-text mb-4">Témoignages</h1>
            <p className="text-xl text-gray-600 mb-6">
              Découvrez les histoires d'amour de nos utilisateurs
            </p>

            <div className="inline-flex flex-col items-center bg-gradient-to-r from-pink-50 to-orange-50 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-5xl font-bold text-gray-800">{averageRating}</span>
                <div className="flex flex-col">
                  <div className="flex">{renderStars(5)}</div>
                  <span className="text-sm text-gray-600 mt-1">{totalReviews} avis vérifiés</span>
                </div>
              </div>
              <button
                onClick={() => navigate('/submit-testimonial')}
                className="mt-4 px-6 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors font-medium text-sm"
              >
                ✍️ Partagez Votre Histoire
              </button>
            </div>
          </div>

          <div className="glass-card p-6 mb-8 bg-gradient-to-r from-blue-50 to-indigo-50">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div>
                <h3 className="font-bold text-gray-800 mb-2">Vous avez trouvé l'amour grâce à A-More AI ?</h3>
                <p className="text-gray-600">Partagez votre expérience et inspirez d'autres célibataires !</p>
              </div>
              <button
                onClick={() => navigate('/submit-testimonial')}
                className="px-6 py-3 bg-pink-500 text-white rounded-xl hover:bg-pink-600 transition-colors font-semibold whitespace-nowrap"
              >
                Partager Mon Témoignage
              </button>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="glass-card p-6 text-center">
              <div className="flex items-center justify-center mb-3">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <div className="text-3xl font-bold text-gray-800 mb-1">{totalReviews}</div>
              <div className="text-gray-600">Couples Formés</div>
            </div>

            <div className="glass-card p-6 text-center">
              <div className="flex items-center justify-center mb-3">
                <Heart className="w-8 h-8 text-pink-500 fill-pink-500" />
              </div>
              <div className="text-3xl font-bold text-gray-800 mb-1">{satisfaction}%</div>
              <div className="text-gray-600">Taux de Satisfaction</div>
            </div>

            <div className="glass-card p-6 text-center">
              <div className="flex items-center justify-center mb-3">
                <Star className="w-8 h-8 text-yellow-400 fill-yellow-400" />
              </div>
              <div className="text-3xl font-bold text-gray-800 mb-1">{averageRating}/5</div>
              <div className="text-gray-600">Note Moyenne</div>
            </div>
          </div>

          {testimonials.length === 0 ? (
            <div className="glass-card p-12 text-center">
              <div className="flex items-center justify-center mb-4">
                <Quote className="w-16 h-16 text-pink-300" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-3">Soyez le premier à partager votre histoire !</h3>
              <p className="text-gray-600 mb-6">
                Votre témoignage peut inspirer d'autres célibataires à trouver l'amour grâce à A-More AI.
              </p>
              <button
                onClick={() => navigate('/submit-testimonial')}
                className="px-8 py-3 bg-pink-500 text-white rounded-xl hover:bg-pink-600 transition-colors font-semibold"
              >
                ✍️ Partager Mon Témoignage
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              {testimonials.map((testimonial) => (
                <article
                  key={testimonial.id}
                  className="glass-card p-6 hover:shadow-lg transition-shadow"
                  itemScope
                  itemType="https://schema.org/Review"
                >
                  <div className="flex items-start gap-4">
                    <img
                      src={testimonial.photo}
                      alt={testimonial.name}
                      className="w-16 h-16 rounded-full flex-shrink-0"
                      itemProp="image"
                    />
                    
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-bold text-gray-800" itemProp="author">{testimonial.name}</h3>
                          <p className="text-sm text-gray-600">{testimonial.age} ans • {testimonial.location}</p>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <div className="flex" itemProp="reviewRating" itemScope itemType="https://schema.org/Rating">
                            <meta itemProp="ratingValue" content={testimonial.rating} />
                            <meta itemProp="bestRating" content="5" />
                            {renderStars(testimonial.rating)}
                          </div>
                        </div>
                      </div>

                      <div className="relative">
                        <Quote className="absolute -top-2 -left-2 w-8 h-8 text-pink-200" />
                        <p className="text-gray-700 leading-relaxed pl-6 mb-3" itemProp="reviewBody">
                          {testimonial.text}
                        </p>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="inline-flex items-center gap-2 px-3 py-1 bg-pink-100 text-pink-700 rounded-full text-sm font-medium">
                          <CheckCircle className="w-4 h-4" />
                          {testimonial.highlight}
                        </div>
                        <time className="text-sm text-gray-500" itemProp="datePublished" dateTime={testimonial.date}>
                          {new Date(testimonial.date).toLocaleDateString('fr-FR', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          })}
                        </time>
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}

          <div className="glass-card p-8 text-center mt-8 bg-gradient-to-r from-pink-500 to-orange-400">
            <h2 className="text-3xl font-bold text-white mb-4">Trouvez l'Amour avec A-More AI</h2>
            <p className="text-white text-lg mb-6 opacity-90">Rejoignez notre communauté et rencontrez votre âme sœur</p>
            <button
              onClick={() => navigate('/auth?mode=signup')}
              className="px-8 py-4 bg-white text-pink-600 rounded-xl font-bold text-lg hover:shadow-2xl transition-shadow"
            >
              Commencer Gratuitement
            </button>
            <p className="text-white text-sm mt-4 opacity-75">🎉 Les 500 premiers inscrits sont gratuits !</p>
          </div>
        </div>
      </div>
    </>
  );
}
